import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Upload, Eye, EyeOff, Pencil, Megaphone } from 'lucide-react';
import { SpotlightModal } from './ConvergeSpotlightModal';
import { ConvergeRsvpAdmin } from './ConvergeRsvpAdmin';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: '', title: '', company: '', photo_url: '', linkedin_url: '', order: 0, is_published: true, role: 'speaker' };
const ROLES = [['speaker', 'Speakers'], ['mentor', 'Mentors'], ['jury', 'Jury']];
const NOUN = { speaker: 'speaker', mentor: 'mentor', jury: 'jury member' };

const SpeakerForm = ({ value, onChange, onSave, onCancel, headers, saving }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/admin/super/converge/speakers/photo`, fd, { headers });
      onChange({ ...value, photo_url: res.data.url });
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
  };
  const field = (k, label, extra = {}) => (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
      <input value={value[k]} onChange={(e) => onChange({ ...value, [k]: extra.type === 'number' ? Number(e.target.value) : e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 rounded text-sm" data-testid={`speaker-input-${k}`} {...extra} />
    </label>
  );
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="speaker-form">
      <div className="grid sm:grid-cols-2 gap-3">
        {field('name', 'Name *')}
        {field('title', 'Title / Role')}
        {field('company', 'Company / Organisation')}
        {field('linkedin_url', 'LinkedIn URL', { placeholder: 'https://www.linkedin.com/in/…' })}
        <label className="block">
          <span className="block text-xs font-semibold text-gray-600 mb-1">Role</span>
          <select value={value.role} onChange={(e) => onChange({ ...value, role: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" data-testid="speaker-input-role">
            <option value="speaker">HR Conclave speaker</option><option value="mentor">Hackathon mentor</option><option value="jury">Hackathon jury</option>
          </select>
        </label>
        {field('order', 'Display order', { type: 'number', min: 0 })}
        <label className="flex items-center gap-2 text-sm mt-5">
          <input type="checkbox" checked={value.is_published} onChange={(e) => onChange({ ...value, is_published: e.target.checked })} data-testid="speaker-input-published" />
          Show on /hackathon
        </label>
      </div>
      <div className="flex items-center gap-3">
        {value.photo_url ? <img src={`${API}${value.photo_url}`} alt="" className="w-14 h-14 object-cover rounded border" /> : <div className="w-14 h-14 rounded border border-dashed bg-gray-50" />}
        <label className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm cursor-pointer">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Photo (JPG/PNG/WebP, max 3MB)
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="speaker-photo-input" />
        </label>
        {value.photo_url && <button type="button" onClick={() => onChange({ ...value, photo_url: '' })} className="text-xs text-red-600">Remove photo</button>}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm bg-gray-100 rounded" data-testid="speaker-cancel-btn">Cancel</button>
        <button type="button" onClick={onSave} disabled={saving || uploading || value.name.trim().length < 2}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50" data-testid="speaker-save-btn">{saving ? 'Saving…' : `Save ${NOUN[value.role || 'speaker']}`}</button>
      </div>
    </div>
  );
};

const PeoplePanel = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState('speaker');
  const [spot, setSpot] = useState(null);
  const [section, setSection] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const [list, sec] = await Promise.all([
        axios.get(`${API}/api/admin/super/converge/speakers`, { headers }),
        axios.get(`${API}/api/admin/super/converge/speakers-section`, { headers }),
      ]);
      setItems(list.data.speakers);
      setSection(sec.data.enabled);
    } catch { toast.error('Failed to load speakers'); } finally { setLoading(false); }
  };
  const toggleSection = async () => {
    setBusy('section');
    try {
      const { data } = await axios.put(`${API}/api/admin/super/converge/speakers-section`, { enabled: !section }, { headers });
      setSection(data.enabled);
      toast.success(data.enabled ? 'Speakers section is ON — visible on /hackathon' : 'Speakers section is OFF — hidden on /hackathon');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update speakers section'); } finally { setBusy(''); }
  };
  const togglePublish = async (s) => {
    setBusy(s.id);
    try {
      const { data } = await axios.patch(`${API}/api/admin/super/converge/speakers/${s.id}/publish`, { is_published: !s.is_published }, { headers });
      setItems((xs) => xs.map((x) => (x.id === s.id ? data.speaker : x)));
      toast.success(`${s.name} ${data.speaker.is_published ? 'is now live' : 'hidden'} on /hackathon`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update visibility'); } finally { setBusy(''); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    const { id, created_at, updated_at, created_by, updated_by, ...body } = editing;
    try {
      if (id) await axios.put(`${API}/api/admin/super/converge/speakers/${id}`, body, { headers });
      else await axios.post(`${API}/api/admin/super/converge/speakers`, body, { headers });
      toast.success(`${NOUN[editing.role || 'speaker'][0].toUpperCase()}${NOUN[editing.role || 'speaker'].slice(1)} saved${editing.is_published ? ' — live on /hackathon' : ''}`);
      setEditing(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((Array.isArray(d) ? d[0]?.msg : d)?.replace(/^Value error, /, '') || 'Save failed');
    } finally { setSaving(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} (${NOUN[s.role || 'speaker']})?`)) return;
    try { await axios.delete(`${API}/api/admin/super/converge/speakers/${s.id}`, { headers }); toast.success(`${s.name} removed`); load(); }
    catch { toast.error('Delete failed'); }
  };

  const shown = items.filter((s) => (s.role || 'speaker') === role);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" /></div>;
  return (
    <div data-testid="converge-speakers-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Speakers, mentors & jury ({items.length})</h3>
          <p className="text-xs text-gray-500">Published people appear instantly on /hackathon. No published speakers → "to be announced" cards; no mentors/jury → that section is hidden.</p>
        </div>
        {!editing && <button onClick={() => setEditing({ ...EMPTY, role, order: shown.length + 1 })} className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-sm font-semibold" data-testid="add-speaker-btn"><Plus size={14} /> Add {NOUN[role]}</button>}
      </div>
      <div className={`flex flex-wrap items-center justify-between gap-3 border rounded-lg p-3 ${section ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`} data-testid="speakers-section-toggle-row">
        <div>
          <p className="text-sm font-semibold text-gray-900">Speakers section on /hackathon: <span data-testid="speakers-section-status">{section ? 'ON' : 'OFF'}</span></p>
          <p className="text-xs text-gray-500">OFF hides the whole HR Conclave speakers grid (including "to be announced" cards). Mentors & jury are unaffected.</p>
        </div>
        <button type="button" role="switch" aria-checked={!!section} aria-label="Toggle speakers section" onClick={toggleSection} disabled={busy === 'section'}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${section ? 'bg-green-600' : 'bg-gray-300'}`} data-testid="speakers-section-toggle">
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${section ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>
      <div className="flex gap-2">
        {ROLES.map(([id, label]) => (
          <button key={id} onClick={() => setRole(id)} className={`px-3 py-1.5 rounded text-sm font-medium ${role === id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid={`people-filter-${id}`}>
            {label} ({items.filter((s) => (s.role || 'speaker') === id).length})
          </button>
        ))}
      </div>
      {editing && <SpeakerForm value={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} headers={headers} saving={saving} />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((s) => (
          <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3" data-testid={`speaker-row-${s.id}`}>
            {s.photo_url ? <img src={`${API}${s.photo_url}`} alt="" className="w-12 h-12 object-cover rounded" /> : <div className="w-12 h-12 rounded bg-gray-100" />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 truncate">{s.name}</p>
              <p className="text-xs text-gray-500 truncate">{[s.title, s.company].filter(Boolean).join(' · ') || '—'}</p>
              <p className="text-[11px] text-gray-400 flex items-center gap-1">{s.is_published ? <><Eye size={11} /> Live</> : <><EyeOff size={11} /> Hidden</>} · order {s.order}</p>
            </div>
            <button onClick={() => togglePublish(s)} disabled={busy === s.id} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-40" title={s.is_published ? 'Hide from /hackathon' : 'Show on /hackathon'} data-testid={`toggle-publish-${s.id}`}>{s.is_published ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            <button onClick={() => setSpot(s)} disabled={!s.is_published} className="p-1.5 text-orange-500 hover:bg-orange-50 rounded disabled:opacity-30" title={s.is_published ? 'Spotlight post' : 'Publish first to create a spotlight post'} data-testid={`spotlight-speaker-${s.id}`}><Megaphone size={14} /></button>
            <button onClick={() => setEditing({ ...EMPTY, ...s })} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="Edit" data-testid={`edit-speaker-${s.id}`}><Pencil size={14} /></button>
            <button onClick={() => remove(s)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Remove" data-testid={`delete-speaker-${s.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      {spot && <SpotlightModal person={spot} headers={headers} onClose={() => setSpot(null)} />}
    </div>
  );
};

export const ConvergeSpeakersTab = ({ token }) => {
  const [sub, setSub] = useState('people');
  return (
    <div>
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        {[['people', 'Speakers, Mentors & Jury'], ['rsvps', 'HR Conclave RSVPs']].map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${sub === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500'}`} data-testid={`converge-subtab-${id}`}>{label}</button>
        ))}
      </div>
      {sub === 'people' ? <PeoplePanel token={token} /> : <ConvergeRsvpAdmin token={token} />}
    </div>
  );
};
